import { rd } from '@passionware/monads';
import { AtellioFeatureGroupDefinition } from 'v5/features';
import { FeatureFlagValues } from 'v5/platform/feature-management/feature-management';
import { WithServices } from 'v5/platform/ts/services';
import { WithFeatureService } from '../FeatureService/FeatureService';
import {
  Permission,
  WithPermissionService
} from '../PermissionService/PermissionService';
import {
  AccessQuery,
  AccessService,
  actionToFeatureMap,
  featureFlaggedPemissions,
  SpecialCapability,
  specialCapabilities
} from './AccessService';

const findFeatureForAction = (action: Permission) =>
  actionToFeatureMap.get(action) ?? null;

const validateFeatureQuery = (feature: string) => {
  if (featureFlaggedPemissions[feature]) {
    throw new Error(
      `Feature '${feature}' requires specific permissions. Use action query instead of feature query.`
    );
  }
};

const isSpecialCapability = (action: Permission | SpecialCapability) =>
  specialCapabilities.includes(action as SpecialCapability);

/**
 * Handle special capability cases that are not in CapabilityEnum
 */
const checkSpecialCapability = (
  action: SpecialCapability,
  getIsSuperUser: () => boolean,
  permissionChecker: (permission: Permission) => boolean
): boolean => {
  // For now, only ManageFeatureFlags is handled
  // In the future, add more cases here if needed
  switch (action) {
    case 'manage_feature_flags':
      return (
        getIsSuperUser() || permissionChecker(Permission.MANAGE_FEATURE_FLAGS)
      );
    default:
      return false;
  }
};

/**
 * Core access control logic that can be used with both reactive and async data sources.
 * Extracted from useCan and can to eliminate duplication.
 */
const checkAccess = (
  accessQuery: AccessQuery,
  permissionChecker: (action: Permission) => boolean,
  featureFlags: FeatureFlagValues<AtellioFeatureGroupDefinition>,
  getIsSuperUser: () => boolean
) => {
  if ('feature' in accessQuery) {
    // It's a feature query - validate first, then check feature flag
    validateFeatureQuery(accessQuery.feature);
    // Feature doesn't require specific permissions, just feature flag
    return !!featureFlags[accessQuery.feature];
  } else if ('action' in accessQuery) {
    // Check if it's a special capability (not in CapabilityEnum)
    if (isSpecialCapability(accessQuery.action)) {
      return checkSpecialCapability(
        accessQuery.action as SpecialCapability,
        getIsSuperUser,
        permissionChecker
      );
    }
    // It's an action query - check permission first, then feature flag if tied
    if (!permissionChecker(accessQuery.action as Permission)) {
      return false;
    }
    const relatedFeature = findFeatureForAction(
      accessQuery.action as Permission
    );
    if (relatedFeature) {
      // Action is tied to a feature - check feature flag
      return !!featureFlags[relatedFeature];
    }
    // Action not tied to any feature, permission check is sufficient
    return true;
  }
  return false;
};

export function createAccessService(
  config: WithServices<[WithFeatureService, WithPermissionService]> & {
    getIsSuperUser: () => boolean;
  }
): AccessService {
  const { featureService, permissionService } = config.services;

  return {
    useCan: () => {
      const hasPermission = permissionService.useHasPermission();
      const featureFlags = featureService.useFeatureFlags();

      return (accessQuery: AccessQuery) =>
        rd.map(featureFlags, flags =>
          checkAccess(accessQuery, hasPermission, flags, config.getIsSuperUser)
        );
    },

    can: async (accessQuery: AccessQuery) => {
      const flags = await featureService.ensureFeatureFlags();
      return checkAccess(
        accessQuery,
        permissionService.hasPermission,
        flags,
        config.getIsSuperUser
      );
    }
  };
}
